import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, TriangleAlert } from "lucide-react";
import { api, money } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { ExportButtons } from "@/components/ExportButtons";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BAND = {
  green: "text-emerald-600",
  amber: "text-amber-600",
  red: "text-crimson-600",
};
const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-crimson-500" };

const SECTIONS = [
  {
    key: "crm",
    title: "CRM team",
    columns: [
      { label: "Staff", value: (r) => r.name },
      { label: "Registrations", value: (r) => r.registrations },
      { label: "On time", value: (r) => r.on_time },
      { label: "Late", value: (r) => r.late },
      { label: "Protection", value: (r) => r.protection_registrations ?? 0 },
      { label: "Feedback", value: (r) => r.feedback_calls ?? 0 },
      { label: "Reviews", value: (r) => r.google_reviews ?? 0 },
      { label: "Calls logged", value: (r) => r.calls_logged },
      { label: "Score", value: (r) => r.score },
    ],
  },
  {
    key: "product",
    title: "Product manager (B2B)",
    columns: [
      { label: "Staff", value: (r) => r.name },
      { label: "Score", value: (r) => r.score },
    ],
  },
  {
    key: "sales",
    title: "Central sales team (B2C)",
    columns: [
      { label: "Staff", value: (r) => r.name },
      { label: "Leads", value: (r) => r.leads_created },
      { label: "Conversions", value: (r) => r.conversions },
      { label: "Conversion %", value: (r) => r.conversion_rate },
      { label: "Follow-up %", value: (r) => r.follow_up_compliance },
      { label: "Alignment %", value: (r) => r.pipeline_alignment },
      { label: "Score", value: (r) => r.score },
    ],
  },
];

export default function CentralPerformance() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState("crm");

  useEffect(() => {
    api
      .get("/performance/central", { params: month ? { month } : {} })
      .then(({ data }) => {
        setData(data);
        if (!month) setMonth(data.month);
      });
  }, [month]);

  const crmOverdue = usePaged(data?.crm.overdue_registrations || []);

  if (!data) return null;

  return (
    <div>
      <PageHead
        title="Central Team Performance"
        subtitle="HQ scorecards by function — CRM, product and central sales"
      >
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="min-h-[44px] w-[150px]" data-testid="performance-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {data.months.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageHead>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.tiles.map((t) => (
          <Card
            key={t.key}
            className="border-brand-line shadow-none"
            data-testid={`perf-tile-${t.key}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    t.has_activity === false ? "bg-slate-300" : DOT[t.band]
                  }`}
                />
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t.label}
                </p>
              </div>
              <p
                className={`mt-2 font-display text-3xl font-semibold ${
                  t.has_activity === false ? "text-slate-400" : BAND[t.band]
                }`}
              >
                {t.has_activity === false ? "—" : t.score}
                {t.has_activity !== false && (
                  <span className="text-base text-slate-400">/100</span>
                )}
              </p>
              {t.has_activity === false && (
                <p className="mt-1 text-xs text-slate-400">No activity recorded this month</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-3">
        {SECTIONS.map((sec) => {
          const block = data[sec.key];
          const isOpen = open === sec.key;
          return (
            <Card key={sec.key} className="border-brand-line shadow-none">
              <button
                data-testid={`perf-section-${sec.key}`}
                onClick={() => setOpen(isOpen ? null : sec.key)}
                className="flex min-h-[44px] w-full items-center justify-between gap-3 px-5 py-4 text-left"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  )}
                  {sec.title}
                </span>
                <span
                  className={`text-sm font-semibold ${
                    block.has_activity === false
                      ? "text-slate-400"
                      : BAND[block.score >= 80 ? "green" : block.score >= 60 ? "amber" : "red"]
                  }`}
                >
                  {block.has_activity === false ? "no activity" : `${block.score}/100`}
                </span>
              </button>

              {isOpen && (
                <CardContent className="border-t border-slate-100 pt-4">
                  {sec.key === "crm" && (
                    <p className="mb-3 text-sm text-slate-600">
                      {block.overdue_count} overdue warranty registrations (delivered 9+ days ago)
                    </p>
                  )}
                  {sec.key === "product" && (
                    <div className="mb-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
                      <p>B2B revenue: <strong>{money(block.b2b_revenue_month)}</strong> (prev {money(block.b2b_revenue_prev_month)})</p>
                      <p>B2B profit: <strong>{money(block.b2b_profit_month)}</strong></p>
                      <p>
                        Orders — submitted {block.orders.Submitted} · confirmed{" "}
                        {block.orders.Confirmed} · delivered {block.orders.Delivered}
                      </p>
                      <p>
                        Tier bands — low {block.tier_bands.low} · medium {block.tier_bands.medium} ·
                        high {block.tier_bands.high}
                      </p>
                      <p className="sm:col-span-2">
                        Declining studios:{" "}
                        {block.declining_studios.length
                          ? block.declining_studios
                              .map((d) => `${d.studio_name} (${d.previous}→${d.score})`)
                              .join(", ")
                          : "none"}
                      </p>
                    </div>
                  )}

                  <div className="mb-3 flex justify-end">
                    <ExportButtons
                      filename={`veloskin-${sec.key}-scorecard-${month}`}
                      title={`${sec.title} scorecard ${month}`}
                      columns={sec.columns}
                      rows={block.members || []}
                      testidPrefix={`perf-${sec.key}`}
                    />
                  </div>

                  <div className="overflow-x-auto">
                    <Table data-testid={`perf-table-${sec.key}`} className="mobile-cards">
                      <TableHeader>
                        <TableRow>
                          {sec.columns.map((c) => (
                            <TableHead key={c.label}>{c.label}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(block.members || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={sec.columns.length} className="py-8 text-center text-slate-500">
                              No staff in this function yet.
                            </TableCell>
                          </TableRow>
                        )}
                        {(block.members || [])
                          .slice()
                          .sort((a, b) => b.score - a.score)
                          .map((m) => (
                            <TableRow key={m.id} className="hover:bg-brand-tint/60" data-testid={`perf-row-${m.id}`}>
                              {sec.columns.map((c) => (
                                <TableCell key={c.label} data-label={c.label}>
                                  {c.value(m)}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>

                  {sec.key === "crm" && block.overdue_registrations.length > 0 && (
                    <div className="mt-6">
                      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-crimson-600">
                        <TriangleAlert className="h-4 w-4" /> Overdue warranty registrations
                      </p>
                      <div className="overflow-x-auto">
                        <Table data-testid="crm-overdue-table" className="mobile-cards">
                          <TableHeader>
                            <TableRow>
                              <TableHead>Customer</TableHead>
                              <TableHead>Studio</TableHead>
                              <TableHead>Delivered</TableHead>
                              <TableHead className="text-right">Days overdue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {crmOverdue.visible.map((o) => (
                              <TableRow key={o.job_id} className="hover:bg-brand-tint/60">
                                <TableCell data-label="Customer" className="font-medium">
                                  {o.customer_name}
                                </TableCell>
                                <TableCell data-label="Studio" className="text-slate-500">
                                  {o.studio_name}
                                </TableCell>
                                <TableCell data-label="Delivered">{o.delivered_on}</TableCell>
                                <TableCell data-label="Days overdue" className="text-right">
                                  <Badge className="border-0 bg-crimson-50 text-crimson-700">
                                    {o.days_overdue}d
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <ShowMore paged={crmOverdue} testid="crm-overdue-show-more" />
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
